import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { PollResponse, ResponseValue, Voter } from '../../types';
import React, { useState } from 'react';
import { SearchParams, searchIndex, splitKeywords } from '../../utils/indexer';
import { Timestamp, addDoc, collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';

import CSVReader from 'react-csv-reader';
import { db } from '../../firebase';
import { v4 as uuidv4 } from 'uuid';

interface Responses {
  [question: string]: ResponseValue;
}

interface ImportError {
  type: string;
  source: string;
  message: string;
  voterName?: string;
  timestamp: Timestamp;
}

const DapaAdditionalCSVImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [fullData, setFullData] = useState<string[][]>([]);

  const handleFileUpload = (data: any[], fileInfo: any) => {
    if (!data || data.length === 0) {
      setError('No data found in the CSV file');
      return;
    }
    
    // Limit preview to first 5 rows
    setPreviewData(data.slice(0, 5));
    // Store full data for import
    setFullData(data);
    setFile(fileInfo);
    setError(null);
  };

  const logImportError = async (errorData: ImportError) => {
    try {
      const importErrorsRef = collection(db, 'importErrors');
      await addDoc(importErrorsRef, errorData);
    } catch (error) {
      console.error('Error logging import error: ', error);
    }
  };

  const submitPollResponse = async (responses: Responses) => {
    try {
      const responseId = uuidv4();
      const pollResponse: PollResponse = {
        id: responseId,
        pollId: "sBB6eh2wcZMZLxNS6rBJ",
        pollTitle: "Dapa (VIS)",
        responses,
        submittedAt: Timestamp.now(),
        submittedBy: "Dapa Import Additional",
      };
      
      const responseRef = doc(collection(db, 'pollResponses'), responseId);
      await setDoc(responseRef, pollResponse);
    } catch (error) {
      console.error('Error submitting poll response: ', error);
      setError('Error submitting poll response: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const getVoterByName = async (voterName: string): Promise<Voter | null> => {
    try {
      const votersColRef = collection(db, 'voters');
      const q = query(votersColRef, where('name', '==', voterName));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data() as Voter;
      }
    } catch (error) {
      console.error('Error checking if voter exists: ', error);
      setError('Error checking if voter exists: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
    return null;
  };

  const searchVoter = async (keyword: string, municipality: string): Promise<Voter | null> => {
    const keywords = splitKeywords(keyword);
    const searchParams: SearchParams = {
      keywords,
      resultLimit: 10,
      document: "voter",
      field: "name",
      searchAfter: null
    };
    let result = await searchIndex(searchParams);
    while ((result.indexes.length <= 0) && (keywords.length > 2)) {
      keywords.pop();
      searchParams.keywords = keywords;
      result = await searchIndex(searchParams);
    }
    
    for (const index of result.indexes) {
      const voterDocRef = doc(db, index.documentPath);
      const voterDoc = await getDoc(voterDocRef);
      if (voterDoc.exists()) {
        const voter = voterDoc.data() as Voter;
        if (voter.city_municipality === municipality) {
          return voter;
        }
      }
    }

    return null;
  };

  const handleImport = async () => {
    debugger;
    if (!file) return;

    setLoading(true);
    setError(null);

    const batchRunTimestamp = Timestamp.now();

    try {
      // Use full data instead of preview data
      const rows = fullData.slice(1); // Skip header row

      for (const row of rows) {
        const isRuayaAdditional: boolean = !!row[1];
        if (!isRuayaAdditional) {
          continue;
        }

        const voterName: string = row[0];
        if (voterName === "") {
          continue;
        }
        let voter = await getVoterByName(voterName);
        voter ??= await searchVoter(voterName, "DAPA");
        if (!voter) {
          // Log error for skipped voter
          await logImportError({
            type: 'VOTER_NOT_FOUND',
            source: 'Dapa Import Additional',
            message: `Voter not found in database`,
            voterName,
            timestamp: batchRunTimestamp
          });
          console.error(`Voter not found in database: ${voterName}`);
          continue;
        }

        const abante: Responses = {
          "DISTRICT 1 REPRESENTATIVE": "Lucille Sering",
          "MAYOR": "Peter Boy Ruaya",
          "PARTY": "Abante Surigao",
          "PROVINCIAL GOVERNOR": "Lyndon Barbers",
          "PROVINCIAL VICE GOVERNOR": "Geed Gokiangkee",
          "VOTER": voter.name,
        };
        await submitPollResponse(abante);
        console.log(`Abante Surigao: ${voterName}`);
      }

      alert('Dapa Additional Poll CSV Import Successful');
      setLoading(false);
    } catch (err) {
      setError('Failed to import CSV');
      console.error(err);
      setLoading(false);
    }
  };

  const papaparseOptions = {
    header: false,
    dynamicTyping: true,
    skipEmptyLines: true,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dapa Municipality CSV Import
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <CSVReader
        cssClass="csv-reader-input"
        onFileLoaded={handleFileUpload}
        parserOptions={papaparseOptions}
      />

      {previewData.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {previewData[0].map((header, index) => (
                    <TableCell key={index}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.slice(1).map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={cellIndex}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={handleImport}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Import CSV'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DapaAdditionalCSVImport;
